import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "../SectionHeading";
import { CASE_STUDIES, IMAGES } from "../../data/content";

const BLOCKS = [
  { key: "challenge", label: "The Challenge" },
  { key: "approach", label: "Our Approach" },
  { key: "solution", label: "The Solution" },
  { key: "outcome", label: "The Outcome" },
];

export default function CaseStudyFeature() {
  const [i, setI] = useState(0);
  const cs = CASE_STUDIES[i];
  const go = (dir) => setI((p) => (p + dir + CASE_STUDIES.length) % CASE_STUDIES.length);

  return (
    <section className="ad-section bg-[#F7F8FA]" id="case-studies" data-testid="case-study-feature">
      <div className="ad-container">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHeading
            eyebrow="Case Studies"
            title={<>See what intelligent data <span className="text-gradient">can make possible.</span></>}
          />
          <div className="flex gap-2">
            <button onClick={() => go(-1)} data-testid="case-prev" aria-label="Previous case study" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#111827]/12 bg-white text-[#111827] transition-colors hover:border-[#1677FF] hover:text-[#1677FF]">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => go(1)} data-testid="case-next" aria-label="Next case study" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#111827]/12 bg-white text-[#111827] transition-colors hover:border-[#1677FF] hover:text-[#1677FF]">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid overflow-hidden rounded-3xl border border-[#111827]/8 bg-white shadow-[0_30px_70px_-45px_rgba(17,24,39,0.4)] lg:grid-cols-2"
            data-testid="case-study-card"
          >
            <div className="img-overlay-ink relative min-h-[280px]">
              <img src={IMAGES[cs.image]} alt={cs.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">{cs.industry}</span>
                <h3 className="mt-2 text-2xl font-extrabold text-white lg:text-3xl">{cs.title}</h3>
              </div>
              {cs.isExample && (
                <span className="absolute right-5 top-5 rounded-full border border-white/40 bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur">
                  Illustrative Example
                </span>
              )}
            </div>

            <div className="p-8 lg:p-10">
              <div className="space-y-5">
                {BLOCKS.map((b) => (
                  <div key={b.key}>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1677FF]">{b.label}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#374151]">{cs[b.key]}</p>
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-1.5 border-t border-[#111827]/8 pt-6">
                {cs.tech.map((t) => (
                  <span key={t} className="rounded-md bg-[#F3F5F7] px-2.5 py-1 text-xs text-[#4B5563]">{t}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
